import { useDomainConfig } from "@/api/cache/appConfigQueryOptions";
import {
  BrandclubStoreConfigUtils, isInstagramOrFBBrowser, PATHNAME_CHECKER, setupDeepView, TrackingParams, useFingerprintJSContext
} from "@brandclub/common-ui";
import { SessionData } from "branch-sdk";
import _get from "lodash/get";
import _omitBy from "lodash/omitBy";
import queryString from "query-string";
import React, { createContext, ReactNode, useEffect, useState } from "react";
import useBranchIOSession from "../hooks/useBranchIOSession";
import { useBCRouting } from "./BCRoutingContextProvider";

type Params<Key extends string = string> = {
  readonly [key in Key]: string | undefined;
};

export const LocationTrackingContext =
  createContext<
    [TrackingParams | null, boolean] | null
  >(null);

export const LocationTrackingContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const bcRouting = useBCRouting();
  const location = bcRouting.location;
  const [hasApp, setHasApp] = useState<boolean>(false);
  const { data: domainConfig } = useDomainConfig();
  const brandPixels = {
    brandId: domainConfig?.brandId!,
    pixels: domainConfig?.pixels!,
  };

  const visitorTracking = useFingerprintJSContext();
  const branchIOSessionData: SessionData | null = useBranchIOSession();
  const [trackingParams, setTrackingParams] =
    useState<TrackingParams | null>(null);

  useEffect(() => {
    if (!visitorTracking?.visitorSessionId) {
      return;
    }
    const appId = BrandclubStoreConfigUtils.getWebAppId();
    const {
      sku,
      referralCode,
      jl_id,

      affiliateLinkId,
      jl_s,
      jl_m, // traffic type = organic / email / partner  / referral
      jl_c, // purpose
      brandclubId,
      brandId,
      brandName,
      invitedBy,
      utm_source,
      utm_medium,
      utm_campaign,
      _branch_referrer, // skip this one from passing
      _branch_match_id,
      ...queryParamsAll
    } = queryString.parse(location?.search) || {};
    const queryParams = _omitBy(queryParamsAll, (_, k) => {
      const key = k?.toLowerCase();
      return key?.startsWith("$og_");
    });
    const skuFromParams = _get(queryParams, ["sku"]);
    let skuFromPath: string | undefined = undefined;
    const pdpMatch = PATHNAME_CHECKER.isPDP(location.pathname);
    if (pdpMatch && pdpMatch[1]) {
      skuFromPath = pdpMatch[1];
    }
    const brandclubIdFromParams = _get(queryParams, ["brandclubId"]);
    const brandIdFromParams = _get(queryParams, ["brandId"]);
    const brandNameFromParams = _get(queryParams, ["brandName"]);
    const newTrackingParams: TrackingParams = {
      ...queryParams,
      appId: appId as string | undefined,
      vs_id: visitorTracking?.visitorSessionId,
      v_id: visitorTracking?.uniqueVisitorId,
      sku: (skuFromPath || sku || skuFromParams) as string | undefined,
      brandclubId: (brandclubId || brandclubIdFromParams) as string | undefined,
      brandId: (brandId || brandIdFromParams) as string | undefined,
      brandName: (brandName || brandNameFromParams) as string | undefined,
      invitedBy: invitedBy as string | undefined,
      referralCode: referralCode as string | undefined,
      brandPixels: brandPixels,
      jl_id: jl_id as string | undefined,
      affiliateLinkId: affiliateLinkId as string | undefined,

      jl_s: jl_s as string | undefined,
      jl_m: jl_m as string | undefined,
      jl_c: jl_c as string | undefined,

      utm_source: utm_source as string | undefined,
      utm_medium: utm_medium as string | undefined,
      utm_campaign: utm_campaign as string | undefined,
      storePlatformType: "web",
      storeDomainName: domainConfig?.domainName,
      storeBrandId: domainConfig?.brandId,
      storeBrandingType: domainConfig?.storeBrandingType,
    };
    window.dispatchEvent(
      new CustomEvent("bc-init", {
        bubbles: true,
        detail: newTrackingParams,
      })
    );
    setTrackingParams(newTrackingParams);
  }, [
    location?.search,
    visitorTracking?.visitorSessionId,
    visitorTracking?.uniqueVisitorId,
    location?.pathname,
    domainConfig,
  ]);

  useEffect(() => {
    const setupBranch = async () => {
      if (!branchIOSessionData) {
        return;
      }

      const data = branchIOSessionData;
      if (data?.has_app) {
        setHasApp(true);
      }
      if (trackingParams) {
        await setupDeepView({
          ...data?.data_parsed,
          ...trackingParams,
          $uri_redirect_mode: isInstagramOrFBBrowser() ? 2 : 0,
          $after_click_url: bcRouting.location.href,
        });
      }
    };

    setupBranch();
  }, [branchIOSessionData, trackingParams]);

  return (
    trackingParams && (
      <LocationTrackingContext.Provider
        value={[trackingParams, hasApp]}
      >
        {children}
      </LocationTrackingContext.Provider>
    )
  );
};

export const useLocationTrackingParams = () => {
  const locationTrackingCtx = React.useContext(LocationTrackingContext);
  if (!locationTrackingCtx) {
    throw new Error(
      "useLocationTrackingParams must be used within a LocationTrackingContextProvider"
    );
  }
  return locationTrackingCtx;
};
